import React from 'react';
import { ListGroup, Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import './ManageTrip.scss';
import { useSelector } from 'react-redux';
import { TripState } from '../../types/trip.store.type';
import moment from 'moment';
import {
    Guests,
    VehicleRegistrationRequiredAction,
    PayBalanceRequiredAction,
    ContractAgreementRequiredAction,
} from 'guest-data-types-ts-lib';

import { FormattedDate } from 'react-intl';
import {
    IconPeopleTwo,
    IconHandshake,
    IconMoneyBill,
    IconCar,
    IconCheckMark,
    IconArrowRightTwo,
    IconDog,
    IconPool,
} from '../../assets/icons';
import { AppRoutes } from '../../routes';
import { TranslationUtils } from '../../utils';

import { ReservationUtils } from '../../utils';
import { AdjustmentConfig, Splits } from '../../types/split.type';
import { isFeatureAvailableForChannel, useFeatureConfig } from '../../services/optimizely/optimizelyHelper';

interface ManagedItem {
    id: string;
    icon: any;
    category: any;
    title: any;
    subtitle: any;
    completed: boolean | null;
    link: string | null;
    showPointer: boolean;
}

export const ManageTrip: React.FC = () => {
    const trip = useSelector((store: { trip: TripState }) => store.trip);
    const {
        isEnabled: isManagePetsAvailable,
        config: managePetsConfig,
        clientReady: managePetClientReady,
    } = useFeatureConfig<AdjustmentConfig>(Splits.GUEST_PORTAL_NEW_PET_ADJUSTMENT, false);

    const history = useHistory();

    const { guests, stay, required_actions, reservation, isPoolHeatAvailable } = trip;

    if (!guests || !stay || !required_actions || !reservation) {
        return null;
    }

    const isPoolHeatPurchased = trip.stay?.pool_heat_purchased;
    const isNewAddPetAvailable = isFeatureAvailableForChannel(reservation!, managePetClientReady, managePetsConfig, isManagePetsAvailable);

    const completeBy = <FormattedDate value={ReservationUtils.getRequiredByDate(stay)} day="numeric" month="short" year="numeric" />;

    const balanceDueBy = (
        <FormattedDate value={ReservationUtils.getBalanceDueDate(stay, reservation)} day="numeric" month="short" year="numeric" />
    );

    const items: ManagedItem[] = [];

    const getGuestsItem = (tripGuests: Guests): ManagedItem => {
        const { current_counts, max_counts } = tripGuests;
        const currentOccupancy = current_counts.adults + current_counts.children;
        const remainingGuests = max_counts.total_people - currentOccupancy;

        const getCurrentOccupancyTitle = (): string => {
            return `${currentOccupancy} ${TranslationUtils.get(`${currentOccupancy === 1 ? 'guest' : 'guests'}`).toLocaleLowerCase()}`;
        };

        const getMaxOccupancySubtitle = (): string => {
            if (remainingGuests >= 1) {
                return TranslationUtils.get('can_add_guest', {
                    total: remainingGuests,
                    guests: TranslationUtils.get(`${remainingGuests === 1 ? 'guest' : 'guests'}`).toLocaleLowerCase(),
                });
            } else {
                return TranslationUtils.get('can_not_add_guest');
            }
        };

        return {
            id: 'manage-trip-guests',
            icon: <IconPeopleTwo className="category-icon" />,
            category: TranslationUtils.get('guests'),
            title: getCurrentOccupancyTitle(),
            subtitle: getMaxOccupancySubtitle(),
            completed: null,
            link: AppRoutes.MODIFY_TRIP,
            showPointer: true,
        };
    };

    const updatePetsItems = (tripGuests: Guests): ManagedItem => {
        const { current_counts, max_counts } = tripGuests;
        const currentPetOccupancy = current_counts.pets;
        const remainingPets = max_counts.pets - currentPetOccupancy;

        const getCurrentOccupancyTitle = (): string => {
            return `${currentPetOccupancy} ${TranslationUtils.get(`${currentPetOccupancy === 1 ? 'pet' : 'pets'}`).toLocaleLowerCase()}`;
        };

        const getMaxOccupancySubtitle = (): string => {
            if (remainingPets >= 1) {
                return currentPetOccupancy === 1
                    ? TranslationUtils.get('can_add_pet', {
                          total: remainingPets,
                      })
                    : TranslationUtils.get('can_add_pets', {
                          total: remainingPets,
                      });
            } else {
                return TranslationUtils.get('can_not_add_pet');
            }
        };

        return {
            id: 'manage-trip-pets',
            icon: <IconDog className="category-icon" />,
            category: TranslationUtils.get('pets'),
            title: getCurrentOccupancyTitle(),
            subtitle: getMaxOccupancySubtitle(),
            completed: null,
            link: AppRoutes.MODIFY_PETS,
            showPointer: true,
        };
    };

    const getRentalAgreementItem = (agreement: ContractAgreementRequiredAction): ManagedItem => {
        const isAgreementComplete = agreement.completed && !!agreement.completed_date;

        let agreementTitle;
        let agreementSubtitle;

        if (isAgreementComplete) {
            const signedOn = (
                <FormattedDate
                    value={moment(new Date(agreement.completed_date as string)).toDate()}
                    day="numeric"
                    month="short"
                    year="numeric"
                />
            );
            agreementTitle = TranslationUtils.get('view_agreement');
            agreementSubtitle = TranslationUtils.get('signed_on', { signedOn });
        } else {
            agreementTitle = TranslationUtils.get('sign_agreement');
            agreementSubtitle = TranslationUtils.get('sign_by', { signBy: completeBy });
        }

        return {
            id: 'manage-trip-rental-agreement',
            icon: <IconHandshake className="category-icon" />,
            category: TranslationUtils.get('rental_agreement'),
            title: agreementTitle,
            subtitle: agreementSubtitle,
            completed: isAgreementComplete,
            link: AppRoutes.RENTAL_AGREEMENT,
            showPointer: true,
        };
    };

    const getPaymentDetailsItem = (balance: PayBalanceRequiredAction): ManagedItem => {
        const isPaymentComplete = balance.completed && +balance.balance_due <= 0;

        let paymentTitle;
        let paymentSubtitle;
        let link: string;

        if (isPaymentComplete) {
            paymentTitle = TranslationUtils.get('view_receipt');
            paymentSubtitle = TranslationUtils.get('payment_complete');
            link = AppRoutes.RECEIPT;
        } else {
            paymentTitle = TranslationUtils.get('make_payment');
            paymentSubtitle = TranslationUtils.get('pay_by', {
                total: `${balance.balance_due} ${balance.currency_code}`,
                payBy: balanceDueBy,
            });
            link = AppRoutes.PAYMENT;
        }
        return {
            id: 'manage-trip-payment',
            icon: <IconMoneyBill className="category-icon" />,
            category: TranslationUtils.get('payment_details'),
            title: paymentTitle,
            subtitle: paymentSubtitle,
            completed: isPaymentComplete,
            link,
            showPointer: true,
        };
    };

    const getVehicleRegistrationItem = (registration: VehicleRegistrationRequiredAction): ManagedItem => {
        const isVehicleRegistrationComplete = registration.completed && !!registration.completed_date;
        let registrationTitle;
        let registrationSubtitle;

        if (isVehicleRegistrationComplete) {
            registrationTitle = TranslationUtils.get('vehicle_added');
        } else {
            registrationTitle = TranslationUtils.get('add_vehicle_info');
            registrationSubtitle = TranslationUtils.get('register_by', { registerBy: completeBy });
        }
        return {
            id: 'manage-trip-vehicle-registration',
            icon: <IconCar className="category-icon" />,
            category: TranslationUtils.get('register_vehicle'),
            title: registrationTitle,
            subtitle: registrationSubtitle,
            completed: isVehicleRegistrationComplete,
            link: AppRoutes.VEHICLE_REGISTRATION,
            showPointer: true,
        };
    };

    const getPoolHeatAddOnItem = (): ManagedItem => {
        return {
            id: 'manage-trip-pool-heat',
            icon: <IconPool className="category-icon" />,
            category: TranslationUtils.get('pool_heat'),
            title: TranslationUtils.get('pool_heat_added'),
            subtitle: '',
            completed: true,
            link: null,
            showPointer: false,
        };
    };

    items.push(getGuestsItem(guests));

    if (isManagePetsAvailable && stay.pets_adjustment_available && isNewAddPetAvailable) {
        items.push(updatePetsItems(guests));
    }

    if (isPoolHeatAvailable) {
        items.push(getPoolHeatAddOnItem());
    }

    required_actions.contract_agreement && items.push(getRentalAgreementItem(required_actions.contract_agreement));
    required_actions.pay_balance && items.push(getPaymentDetailsItem(required_actions.pay_balance));

    if (required_actions.vehicle_registration) {
        items.push(getVehicleRegistrationItem(required_actions.vehicle_registration));
    }

    if (isPoolHeatPurchased) {
        items.push(getPoolHeatAddOnItem());
    }

    return (
        <Card className="panel manage-trip-panel">
            <ListGroup variant="flush">
                {items.map((item, i) => (
                    <ListGroup.Item key={i}>
                        <div
                            tabIndex={0}
                            role="button"
                            className="managed-item"
                            id={item.id}
                            onKeyPress={() => item.link && history.push(item.link)}
                            onClick={() => item.link && history.push(item.link)}
                        >
                            <div className="category">
                                <div className="d-none d-md-block"> {item.icon}</div>
                                <span className="body-regular">{item.category}</span>
                            </div>
                            <div className="item-notification">
                                {item.completed === true ? (
                                    <div className="complete-notification">
                                        <IconCheckMark />
                                    </div>
                                ) : item.completed === false ? (
                                    <div className="circle-notification" />
                                ) : null}
                            </div>
                            <div className="block">
                                <p className="body-regular title">{item.title}</p>
                                {item.subtitle && <span className="body-xs subtitle">{item.subtitle}</span>}
                            </div>
                            {item.showPointer && (
                                <div className="item-next">
                                    <IconArrowRightTwo className="pointer" />
                                </div>
                            )}
                        </div>
                    </ListGroup.Item>
                ))}
            </ListGroup>
        </Card>
    );
};
